<template>
  <FixedFlex :top="{ consumeAllAvailableSpace: false }">
    <template #top>
      <ErrorMessage
        v-if="outOfDataVisible"
        @click="onClickErrorMessageButton"
      />

      <Chart
        v-show="initialized"
        ref="chart"
        class="chart"
        :style="{ 'min-height': height, 'max-height': height }"
        @afterSetExtremes="afterSetextremes"
      />

      <content-placeholders
        v-show="!initialized"
        class="chart-placeholder"
        :rounded="true"
      >
        <content-placeholders-img
          :style="{ 'min-height': height, 'max-height': height }"
        />
      </content-placeholders>
    </template>

    <template #bottom>
      <div class="bottom-container">
        <div
          v-show="initialized"
          class="stat-panes-container"
          :class="{ vertical: true, horizontal: false }"
        >
          <PaneInfo
            v-for="(data, key) in stat"
            v-bind="{ data, vertical: true, horizontal: false }"
            :key="key"
            :active="sensorsState[key].active"
            @input="onInput($event, key)"
          />
        </div>

        <content-placeholders
          v-show="!initialized"
          :rounded="true"
        >
          <content-placeholders-heading />
          <content-placeholders-heading />
          <content-placeholders-heading />
        </content-placeholders>
      </div>
    </template>
  </FixedFlex>
</template>


<script>
import PublicPackageApi from '@/api/public';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import Chart from './Chart';

import PaneInfo from './PaneInfo';
import { createSeries } from './Chart/utils';
import { createPoints } from './utils';
import FixedFlex from '@/components/FixedFlex';
import ErrorMessage from './ErrorMessage';

export default {
  components: {
    Chart,
    PaneInfo,
    FixedFlex,
    ErrorMessage,
  },

  data() {
    return {
      height: null,
      // TODO : temp
      outOfDataVisible: false,
      initialized: false,
      chartData: {},

      chartWindow: {
        startDate: null,
        stopDate: null,
      },

      blockExtremus: false,
    };
  },
  computed: {
    ...mapGetters({
      sensorUid: 'shipments/sensorUid',
      stat: 'packageDetails/stat/stat',
      sensorsState: 'packageDetails/stat/sensorsState',
      sensorTypes: 'sensorTypes/listByKey',
    }),
  },

  watch: {
    outOfDataVisible() {
      const { chart } = this.$refs;
      chart.adjustChartHeight();
    },
  },
  // Height of chart must be same as map height (35% of view without top/bottom menus)
  beforeMount() {
    const height = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight || 0
    );

    const TOP_MENU_HEIGHT = 50;
    const BOTTOM_MENU_HEIGHT = 70;

    const viewHeight = height - TOP_MENU_HEIGHT - BOTTOM_MENU_HEIGHT;

    this.height = `${(35 * viewHeight) / 100}px`; // 35%
  },

  async mounted() {
    this.initChart();
    this.changeParams({ hash: this.sensorUid });
    this.getStat();
  },

 

  methods: {
    ...mapActions({
      getStat: 'packageDetails/stat/getStat',
      changeParams: 'packageDetails/stat/changeParams',
    }),
    ...mapMutations({
      setSensorActivity: 'packageDetails/stat/setSensorActivity',
    }),

    onClickErrorMessageButton() {
      this.outOfDataVisible = !this.outOfDataVisible;
    },

    onInput(value, key) {
      this.setSensorActivity({ key, value });
      const { chart } = this.$refs;
      chart.setSeriesVisibility(key, value);
      const filteredSensorsState = Object.entries(this.sensorsState).filter(
        (sensorStateItem) => sensorStateItem[1].active
      );
      if (filteredSensorsState.length == 1) {
        const key = filteredSensorsState[0][0];
        chart.setYAxisLabelsVisibility(key, true);
      } else {
        chart.turnOffYAxisLabels();
      }
    },

    initChartData() {
      for (let key in this.sensorTypes) {
        this.$set(this.chartData, key, {
          visble: true,
          sourceDataItems: [],
          data: [],
        });
      }
    },

    // TODO: Move to utils
    normalizeBackendData(data) {
      for (let dataItem of data) {
        const { start, stop } = dataItem;
        for (const key in this.chartData) {
          const { sourceDataItems } = this.chartData[key];
          const sensorValues = dataItem[key];
          if (sensorValues) {
            sourceDataItems.push({ ...dataItem[key], start, stop });
          }
        }
      }
    },

    // TODO: Move to utils ?
    // TODO: Fix duplicates
    initChartSeriesData() {
      for (const key in this.chartData) {
        for (const dataValues of this.chartData[key].sourceDataItems) {
          this.chartData[key].data.push(
            ...createPoints(dataValues, this.chartData)
          );
        }
        this.chartData[key].data.sort((a, b) => a.x - b.x);
      }
    },

    fillChartSeries() {
      const { chart } = this.$refs;
      const highchartsInstance = chart.getChartInstance();

      const seriesesToAdd = [];
      for (const key in this.chartData) {
        const { data } = this.chartData[key];
        const { name, color } = this.sensorTypes[key];
        const series = highchartsInstance.get(key);
        if (!series) {
          const s = createSeries({
            name,
            color,
            data,
            id: key,
          });
          seriesesToAdd.push(s);
          continue;
        }
        series.setData(data, false, false, true);
      }
      chart.addSeriesWithDefaultAxis(seriesesToAdd);
    },

    async fetchChartList(params) {
      const { samples, start_date, stop_date } = params;
      const { data } = await PublicPackageApi.getChart({
        hash: this.sensorUid,
        samples,
        start_date,
        stop_date,
      });
      return data;
    },

    initChart(params) {
      if (this.initialized) {
        return;
      }
      this.$nextTick(async () => {
        const data = await this.fetchChartList({ samples: 120, ...params });
        this.initChartData();
        this.normalizeBackendData(data);
        this.initChartSeriesData();
        this.fillChartSeries();
        this.initialized = true;
      });
    },

    afterSetextremes() {
      return;
      // const LOADING_TEXT = "Loading data from server...";
      // let series;
      // for (const key in this.chartData) {
      //   if (this.chartData[key].data) {
      //     series = this.chartData[key].data;
      //     break;
      //   }
      // }

      // const firstPoint = series[0];
      // const lastPoint = series[series.length - 1];

      // if (!firstPoint || !lastPoint) {
      //   return;
      // }

      // if (firstPoint.x == e.min && lastPoint.x == e.max) {
      //   // Initial extremes setted
      //   return;
      // }

      // const highchartInstance = this.$refs.chart.getChartInstance();

      // highchartInstance.showLoading(LOADING_TEXT);

      // const handler = async () => {
      //   const start_date = Moment(e.min, "x").toISOString();
      //   const stop_date = Moment(e.max, "x").toISOString();
      //   if (
      //     this.chartWindow.startDate == e.min &&
      //     this.chartWindow.stopDate == e.max
      //   ) {
      //     return;
      //   }

      //   this.chartWindow.startDate = e.min;
      //   this.chartWindow.stopDate = e.max;

      //   // this.initialized = false;

      //   const data = await this.fetchChartList({
      //     samples: 20,
      //     start_date,
      //     stop_date,
      //   });

      //   this.normalizeBackendData(cleanData(data));
      //   this.initChartSeriesData();
      //   this.fillChartSeries();
      //   // this.initialized = true;
      // };

      // if (this.blockExtremus) {
      //   return;
      // }

      // this.blockExtremus = true;
      // handler().then(() => {
      //   this.blockExtremus = false;
      //   highchartInstance.hideLoading();
      // });
    },
  },
};
</script>

<style scoped lang="scss">
.chart-placeholder {
  margin: 8px;
}

.panes-placeholders {
  flex: 1;
}

.bottom-container {
  overflow: auto;
  width: 100%;

  .vue-content-placeholders {
    margin-left: 8px;
    margin-right: 8px;
  }
}

.stat-panes-container {
  width: 100%;

  margin-top: 16px;

  padding-left: 10px;
  padding-right: 10px;

  display: flex;
  justify-content: flex-start;

  overflow-y: auto;

  &.vertical {
    flex-direction: column;
  }

  &.horizontal {
    flex-direction: row;
  }
}
</style>